// ─── Types ──────────────────────────────────────────────

export interface PermissionDef {
  key: string;
  label: string;
  description: string;
  category: string;
}

export interface RoleDef {
  key: string;
  label: string;
  description: string;
  scope: 'org' | 'team' | 'self';
  type: 'system' | 'team_position';
  permissions: string[];
  color: string;
  callouts?: string[];
}

// ─── Permission Registry ────────────────────────────────

export const PERMISSIONS: PermissionDef[] = [
  // User Management
  { key: 'users.create', label: 'Create Users', description: 'Create new user accounts', category: 'User Management' },
  { key: 'users.edit', label: 'Edit Users', description: 'Edit user profiles and settings', category: 'User Management' },
  { key: 'users.delete', label: 'Delete Users', description: 'Delete or deactivate accounts', category: 'User Management' },
  { key: 'users.verify', label: 'Verify Users', description: 'Verify/unverify accounts', category: 'User Management' },
  { key: 'users.view_all', label: 'View All Users', description: 'View all user profiles', category: 'User Management' },
  { key: 'users.manage_capacity', label: 'Manage Capacity', description: 'Edit capacity and cost', category: 'User Management' },

  // Team Management
  { key: 'teams.create', label: 'Create Teams', description: 'Create new teams', category: 'Team Management' },
  { key: 'teams.edit', label: 'Edit Teams', description: 'Edit team details', category: 'Team Management' },
  { key: 'teams.delete', label: 'Delete Teams', description: 'Delete teams', category: 'Team Management' },
  { key: 'teams.manage_members', label: 'Manage Members', description: 'Add/remove members, change positions', category: 'Team Management' },

  // Project Management
  { key: 'projects.create', label: 'Create Projects', description: 'Create new projects', category: 'Project Management' },
  { key: 'projects.edit', label: 'Edit Projects', description: 'Edit project details', category: 'Project Management' },
  { key: 'projects.archive', label: 'Archive Projects', description: 'Archive/unarchive projects', category: 'Project Management' },
  { key: 'projects.manage_assignments', label: 'Manage Assignments', description: 'Assign users to projects', category: 'Project Management' },

  // Client Management
  { key: 'clients.view', label: 'View Clients', description: 'View client records', category: 'Client Management' },
  { key: 'clients.create', label: 'Create Clients', description: 'Create client records', category: 'Client Management' },
  { key: 'clients.edit', label: 'Edit Clients', description: 'Edit client details', category: 'Client Management' },
  { key: 'clients.delete', label: 'Delete Clients', description: 'Delete client records', category: 'Client Management' },

  // Leave Management
  { key: 'leave.approve', label: 'Approve Leaves', description: 'Approve/reject leave requests', category: 'Leave Management' },
  { key: 'leave.manage_types', label: 'Manage Leave Types', description: 'Create/edit/delete leave types', category: 'Leave Management' },
  { key: 'leave.manage_calendars', label: 'Manage Calendars', description: 'Manage holiday calendars', category: 'Leave Management' },
  { key: 'leave.manage_balances', label: 'Manage Balances', description: 'Adjust leave balances', category: 'Leave Management' },
  { key: 'leave.view_reports', label: 'View Leave Reports', description: 'Org-wide leave reports', category: 'Leave Management' },
  { key: 'leave.view_team', label: 'View Team Leave', description: 'View team member leave requests', category: 'Leave Management' },

  // Work Location
  { key: 'location.approve', label: 'Approve Locations', description: 'Approve/reject location requests', category: 'Work Location' },
  { key: 'location.view_team', label: 'View Team Locations', description: 'View team work location requests', category: 'Work Location' },

  // Timesheet
  { key: 'timesheet.view_team', label: 'Team Timesheets', description: 'View team member timesheets', category: 'Timesheet' },

  // Analytics
  { key: 'analytics.view_team', label: 'Team Analytics', description: 'View team analytics', category: 'Analytics' },
  { key: 'analytics.view_org', label: 'Org Analytics', description: 'View org-wide analytics', category: 'Analytics' },
  { key: 'analytics.view_cost', label: 'Cost Analysis', description: 'View cost/financial reports', category: 'Analytics' },
  { key: 'analytics.compare_teams', label: 'Cross-Team', description: 'Compare metrics across teams', category: 'Analytics' },

  // Effort Reports
  { key: 'effort.view_team', label: 'Team Effort', description: 'View team effort reports', category: 'Effort Reports' },
  { key: 'effort.view_org', label: 'Org Effort', description: 'View all effort reports', category: 'Effort Reports' },

  // Content
  { key: 'content.manage_news', label: 'Manage News', description: 'CRUD news articles', category: 'Content' },
  { key: 'content.manage_surveys', label: 'Manage Surveys', description: 'CRUD surveys + responses', category: 'Content' },
  { key: 'content.manage_training', label: 'Manage Training', description: 'CRUD training + learning paths', category: 'Content' },
  { key: 'content.manage_policies', label: 'Manage Policies', description: 'CRUD policies', category: 'Content' },
  { key: 'content.manage_events', label: 'Manage Events', description: 'Create and manage events', category: 'Content' },

  // Resources
  { key: 'resources.view_team', label: 'Team Resources', description: 'View team resource allocation', category: 'Resources' },
  { key: 'resources.view_org', label: 'Org Resources', description: 'View org resource allocation', category: 'Resources' },
  { key: 'resources.manage', label: 'Manage Resources', description: 'Manage resource assignments', category: 'Resources' },

  // Tasks
  { key: 'tasks.view_team', label: 'View Team Tasks', description: 'View team member tasks', category: 'Tasks' },
  { key: 'tasks.assign_team', label: 'Assign Tasks', description: 'Assign tasks to team members', category: 'Tasks' },

  // Admin
  { key: 'settings.manage', label: 'Admin Settings', description: 'Access admin settings', category: 'Admin' },
  { key: 'access.manage_options', label: 'Manage Access Options', description: 'Manage request types, projects and instances for access requests', category: 'Admin' },
];

// ─── System Roles ───────────────────────────────────────

export const SYSTEM_ROLES: RoleDef[] = [
  {
    key: 'superadmin',
    label: 'Super Admin',
    description: 'System owner — all admin privileges plus AI analytics & system tools',
    scope: 'org',
    type: 'system',
    permissions: PERMISSIONS.map(p => p.key),
    color: 'amber',
  },
  {
    key: 'admin',
    label: 'Admin',
    description: 'Full system access — bypasses all permission checks',
    scope: 'org',
    type: 'system',
    permissions: PERMISSIONS.map(p => p.key),
    color: 'red',
  },
  {
    key: 'leave_manager',
    label: 'Leave Manager',
    description: 'Org-wide leave management and approvals',
    scope: 'org',
    type: 'system',
    permissions: [
      'leave.approve', 'leave.manage_types', 'leave.manage_calendars',
      'leave.manage_balances', 'leave.view_reports', 'location.approve',
    ],
    color: 'green',
  },
  {
    key: 'full_analytics',
    label: 'Full Analytics',
    description: 'Read-only access to all org-wide analytics and reports',
    scope: 'org',
    type: 'system',
    permissions: [
      'users.view_all', 'analytics.view_team', 'analytics.view_org',
      'analytics.view_cost', 'analytics.compare_teams',
      'effort.view_team', 'effort.view_org',
      'leave.view_reports', 'resources.view_org',
    ],
    color: 'blue',
  },
  {
    key: 'content_manager',
    label: 'Content Manager',
    description: 'Manages news, surveys, training, policies, events',
    scope: 'org',
    type: 'system',
    permissions: [
      'content.manage_news', 'content.manage_surveys',
      'content.manage_training', 'content.manage_policies',
      'content.manage_events',
    ],
    color: 'purple',
  },
  {
    key: 'user',
    label: 'User',
    description: 'Standard employee — personal dashboard, tasks, leave',
    scope: 'self',
    type: 'system',
    permissions: [],
    color: 'gray',
  },
];

// ─── Team Positions ─────────────────────────────────────

export const TEAM_POSITIONS: RoleDef[] = [
  {
    key: 'team_head',
    label: 'Team Head',
    description: 'Team-scoped admin — manages users, projects, sub-teams, approvals, and reports for their team',
    scope: 'team',
    type: 'team_position',
    permissions: [
      // Read/visibility
      'users.view_all',
      'leave.view_team', 'location.view_team', 'timesheet.view_team',
      'effort.view_team', 'tasks.view_team',
      'analytics.view_team', 'resources.view_team',
      // Approvals
      'leave.approve', 'location.approve',
      // Team-scoped admin actions
      'users.edit', 'users.verify', 'users.manage_capacity',
      'teams.create', 'teams.edit', 'teams.manage_members',
      'projects.create', 'projects.edit', 'projects.archive', 'projects.manage_assignments',
      'leave.manage_balances', 'leave.view_reports',
      'tasks.assign_team',
      'resources.manage',
      // Content authorship — News, Training, Knowledge Base (policies), Surveys
      'content.manage_news', 'content.manage_training',
      'content.manage_policies', 'content.manage_surveys',
    ],
    color: 'orange',
    callouts: [
      'Team-scoped admin — can manage users, projects, sub-teams, approvals, and reports for their team',
      'Cannot manage org-wide settings, content, clients, or cross-team analytics',
    ],
  },
  {
    key: 'team_lead',
    label: 'Team Lead',
    description: 'Team visibility — view effort, tasks, analytics (no approvals)',
    scope: 'team',
    type: 'team_position',
    permissions: [
      'leave.view_team', 'location.view_team',
      'timesheet.view_team',
      'effort.view_team', 'tasks.view_team',
      'analytics.view_team', 'resources.view_team',
    ],
    color: 'yellow',
    callouts: ['CANNOT approve/reject leave or work location requests'],
  },
  {
    key: 'member',
    label: 'Member',
    description: 'Regular team member — personal tasks and data only',
    scope: 'self',
    type: 'team_position',
    permissions: [],
    color: 'gray',
  },
];

// ─── Helpers ────────────────────────────────────────────

/** Returns true if the role has full admin privileges (admin or superadmin). */
export function isAdminRole(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'superadmin';
}

/** Returns true only for the superadmin role. */
export function isSuperAdminRole(role: string | undefined | null): boolean {
  return role === 'superadmin';
}

export function getSystemRolePermissions(roleKey: string): string[] {
  return SYSTEM_ROLES.find(r => r.key === roleKey)?.permissions ?? [];
}

export function getTeamPositionPermissions(posKey: string): string[] {
  return TEAM_POSITIONS.find(p => p.key === posKey)?.permissions ?? [];
}

export function roleHasPermission(roleKey: string, perm: string): boolean {
  const sr = SYSTEM_ROLES.find(r => r.key === roleKey);
  if (sr) return sr.permissions.includes(perm);
  const tp = TEAM_POSITIONS.find(p => p.key === roleKey);
  if (tp) return tp.permissions.includes(perm);
  return false;
}

export function getPermissionCategories(): string[] {
  const cats = new Set(PERMISSIONS.map(p => p.category));
  return Array.from(cats);
}

export function getPermissionsByCategory(category: string): PermissionDef[] {
  return PERMISSIONS.filter(p => p.category === category);
}

// ─── Shared Permission Resolution ──────────────────────
// Single source of truth for resolving a user's effective permissions.
// Used by BOTH frontend AuthContext and server-side AI agent.
// No DB queries — callers pre-fetch data and pass it in.

export interface TeamMembershipInput {
  teamId: number;
  position: string;  // 'team_head' | 'team_lead' | 'member'
  teamName?: string; // optional, used for prompt/display generation
}

export interface ResolvedPermissions {
  /** All effective permission keys (union of system role + custom roles + team positions) */
  permissions: string[];
  /** Whether user has admin/superadmin bypass */
  isAdmin: boolean;
  /** Team IDs where user holds team_head position */
  managedTeamIds: number[];
  /** Team IDs where user holds team_lead position */
  leadTeamIds: number[];
  /** All team IDs user belongs to */
  allTeamIds: number[];
  /** User's system role key */
  systemRole: string;
  /** User's team positions by team: teamId → position */
  teamPositions: Map<number, string>;
}

/**
 * Resolves effective permissions from pre-fetched data.
 * Merges: system role permissions + custom role permissions + team position permissions.
 */
export function resolveUserPermissions(
  userRole: string,
  teamMemberships: TeamMembershipInput[],
  customRolePermissions: string[]
): ResolvedPermissions {
  const admin = isAdminRole(userRole);

  // 1. System role permissions
  const systemPerms = getSystemRolePermissions(userRole);

  // 2. Team position permissions
  const teamPerms: string[] = [];
  const managedTeamIds: number[] = [];
  const leadTeamIds: number[] = [];
  const allTeamIds: number[] = [];
  const teamPositions = new Map<number, string>();

  for (const tm of teamMemberships) {
    allTeamIds.push(tm.teamId);
    teamPositions.set(tm.teamId, tm.position);
    const posPerms = getTeamPositionPermissions(tm.position);
    teamPerms.push(...posPerms);
    if (tm.position === 'team_head') managedTeamIds.push(tm.teamId);
    if (tm.position === 'team_lead') leadTeamIds.push(tm.teamId);
  }

  // 3. Union all sources, deduplicate
  const permissions = Array.from(new Set([
    ...systemPerms,
    ...customRolePermissions,
    ...teamPerms,
  ]));

  return {
    permissions,
    isAdmin: admin,
    managedTeamIds,
    leadTeamIds,
    allTeamIds,
    systemRole: userRole,
    teamPositions,
  };
}

/**
 * Check if resolved permissions include a specific permission.
 * Admin bypass returns true for everything.
 */
export function hasResolvedPermission(
  resolved: ResolvedPermissions,
  permission: string
): boolean {
  if (resolved.isAdmin) return true;
  return resolved.permissions.includes(permission);
}

// ─── V1-to-V2 Mapping ──────────────────────────────────

export interface V1User {
  role: string;
  canManageLeaveHr?: boolean;
  canViewOrgAnalytics?: boolean;
  canManageContent?: boolean;
}

export function mapV1UserToV2Roles(user: V1User): string[] {
  if (user.role === 'admin' || user.role === 'superadmin') return [user.role];

  const roles: string[] = [];
  if (user.canManageLeaveHr) roles.push('leave_manager');
  if (user.canViewOrgAnalytics) roles.push('full_analytics');
  if (user.canManageContent) roles.push('content_manager');
  if (roles.length === 0) roles.push('user');
  return roles;
}

export function getV2RoleLabel(user: V1User): string {
  const roles = mapV1UserToV2Roles(user);
  return roles.map(r => SYSTEM_ROLES.find(sr => sr.key === r)?.label ?? r).join(', ');
}

// ─── Config Validation ──────────────────────────────────

export function validateConfig(): string[] {
  const errors: string[] = [];
  const permKeys = new Set(PERMISSIONS.map(p => p.key));

  const seen = new Set<string>();
  for (const p of PERMISSIONS) {
    if (seen.has(p.key)) errors.push(`Duplicate permission key: ${p.key}`);
    seen.add(p.key);
  }

  for (const role of [...SYSTEM_ROLES, ...TEAM_POSITIONS]) {
    if (role.key === 'admin' || role.key === 'superadmin') continue; // admin/superadmin get all dynamically
    for (const perm of role.permissions) {
      if (!permKeys.has(perm)) {
        errors.push(`Role "${role.key}" references unknown permission: ${perm}`);
      }
    }
  }

  return errors;
}
